import React, { useMemo } from 'react';
import { Button, ButtonGroup, Checkbox, Table } from 'rsuite';
import PermissionGate from '../shared/PermissionGate';
import ExpenseStatusBadge from './ExpenseStatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { getCategoryLabel, isAllHandsMealCategory } from '../../utils/expenseCategories';
import { describeExpenseGroup } from '../../utils/expenseGroups';
import './ExpenseList.css';

const { Column, HeaderCell, Cell } = Table;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = DATE_ONLY_PATTERN.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      const localDate = new Date(year, monthIndex, day);
      return localDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Group header rows and the payroll total row carry no expense of their own, so
// every per-expense cell blanks out for them.
const isSummaryRow = (rowData) => Boolean(rowData?.isTotalRow || rowData?.isGroupRow);

// The header row that opens a run of grouped expenses. It reuses the row the
// table already draws — the group needs a name and a total, not a card.
const GroupRowLabel = ({ group }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div className="expense-list__group-name">
      <i className="bi bi-collection me-1" aria-hidden="true" />
      {group.name}
    </div>
    <div className="expense-list__group-meta">
      {group.count} expense{group.count === 1 ? '' : 's'}
      {group.isSubmitted ? '' : ' • draft'}
    </div>
  </div>
);

export default function ExpenseList({
  expenses,
  onView,
  onOpenGroup,
  onManageAttachments,
  onReview,
  onApprove,
  onReject,
  onMarkPaid,
  onMarkInvoiced,
  onEditInvoiceCap,
  onToggleQboReceipt,
  onCancel,
  showEmployeeInfo = false,
  showApprovalDetails = false,
  showPaidOn = true,
  showInvoicedOn = false,
  showInvoiceCap = false,
  showQboReceipt = false,
  showActions = true,
  showApprovalActions = true,
  showCancelAction = true,
  actionsColumnWidth = 300,
}) {
  const auth = useAuth();
  const data = useMemo(() => expenses || [], [expenses]);
  const currentEmployeeId = auth.employee?.id;

  return (
    <Table
      autoHeight
      data={data}
      bordered
      cellBordered
      hover
      rowKey="id"
      // Group headers carry two lines (name, then size); everything else keeps
      // rsuite's default row height.
      rowHeight={(rowData) => (rowData?.isGroupRow ? 58 : 46)}
      rowClassName={(rowData) => {
        if (rowData?.isGroupRow) return 'expense-list__group-row';
        if (!rowData?.groupRowId) return '';
        return `expense-list__group-member${rowData.isLastInGroup ? ' expense-list__group-member--last' : ''}`;
      }}
      onRowClick={(rowData) => {
        if (rowData?.isTotalRow) return;
        if (rowData?.isGroupRow) {
          onOpenGroup?.(rowData.group);
          return;
        }
        onView?.(rowData);
      }}
    >
      {showEmployeeInfo ? (
        <Column width={180}>
          <HeaderCell>Employee</HeaderCell>
          <Cell>
            {(rowData) => {
              if (rowData.isTotalRow) return <strong>Total</strong>;
              if (rowData.isGroupRow) return <GroupRowLabel group={rowData.group} />;
              const first = rowData.first_name || rowData.employee_first_name || '';
              const last = rowData.last_name || rowData.employee_last_name || '';
              return `${first} ${last}`.trim();
            }}
          </Cell>
        </Column>
      ) : null}

      <Column width={150}>
        <HeaderCell>Purchase Date</HeaderCell>
        <Cell>
          {(rowData) => {
            // Without an Employee column the group's name has to land here, in
            // whichever column comes first.
            if (rowData.isGroupRow) {
              return showEmployeeInfo ? '' : <GroupRowLabel group={rowData.group} />;
            }
            return rowData.isTotalRow ? '' : formatDate(rowData.purchase_date);
          }}
        </Cell>
      </Column>

      {showPaidOn ? (
        <Column width={140}>
          <HeaderCell>Paid On</HeaderCell>
          <Cell>{(rowData) => (isSummaryRow(rowData) ? '' : formatDate(rowData.paid_on))}</Cell>
        </Column>
      ) : null}

      {showInvoicedOn ? (
        <Column width={140}>
          <HeaderCell>Invoiced On</HeaderCell>
          <Cell>{(rowData) => (isSummaryRow(rowData) ? '' : formatDate(rowData.invoiced_on))}</Cell>
        </Column>
      ) : null}

      <Column width={180}>
        <HeaderCell>Vendor</HeaderCell>
        <Cell>{(rowData) => (isSummaryRow(rowData) ? '' : rowData.vendor_name)}</Cell>
      </Column>

      <Column width={140}>
        <HeaderCell>Category</HeaderCell>
        <Cell>{(rowData) => (isSummaryRow(rowData) ? '' : getCategoryLabel(rowData.category))}</Cell>
      </Column>

      <Column width={130}>
        <HeaderCell>Amount</HeaderCell>
        <Cell>
          {(rowData) => {
            if (rowData.isGroupRow) return <strong>{formatCurrency(rowData.group.total)}</strong>;
            return rowData.isTotalRow ? <strong>{formatCurrency(rowData.amount)}</strong> : formatCurrency(rowData.amount);
          }}
        </Cell>
      </Column>

      {/* Right after Amount, because the pair only reads as "paid this, billed
          that" side by side. An empty cell is the common case — most items are
          not capped — so it stays a dash rather than a repeat of the amount. */}
      {showInvoiceCap ? (
        <Column width={130}>
          <HeaderCell>Invoice Cap</HeaderCell>
          <Cell>
            {(rowData) => {
              if (isSummaryRow(rowData)) return '';
              const label = rowData.invoice_cap_amount == null ? '—' : formatCurrency(rowData.invoice_cap_amount);
              // The row itself opens the expense for review, so editing the cap
              // needs its own target and has to keep the click to itself.
              if (!onEditInvoiceCap) return label;
              return (
                <Button
                  appearance="link"
                  size="xs"
                  className="p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditInvoiceCap(rowData);
                  }}
                  title="Edit the invoice cap"
                >
                  {label}
                </Button>
              );
            }}
          </Cell>
        </Column>
      ) : null}

      <Column width={130}>
        <HeaderCell>Status</HeaderCell>
        <Cell>{(rowData) => (isSummaryRow(rowData) ? '' : <ExpenseStatusBadge status={rowData.status} />)}</Cell>
      </Column>

      <Column flexGrow={1} minWidth={220}>
        <HeaderCell>Description</HeaderCell>
        <Cell>
          {(rowData) => {
            if (rowData.isGroupRow) {
              return <span className="expense-list__group-meta">{describeExpenseGroup(rowData.group)}</span>;
            }
            return rowData.isTotalRow ? '' : rowData.description;
          }}
        </Cell>
      </Column>

      {showApprovalDetails ? (
        <Column width={240}>
          <HeaderCell>Approval</HeaderCell>
          <Cell>
            {(rowData) => {
              if (isSummaryRow(rowData)) return '';
              const status = String(rowData.status || '').toLowerCase();
              if (status !== 'approved') return '';

              // An All Hands meal is filed approved with no reviewer behind it.
              const approverName = `${rowData.approver_first_name || ''} ${rowData.approver_last_name || ''}`.trim()
                || rowData.approver_email
                || rowData.approved_by
                || (isAllHandsMealCategory(rowData.category) ? 'Pre-approved' : 'Unknown');
              const approvedAt = formatDateTime(rowData.approved_at);

              return (
                <div style={{ lineHeight: 1.25 }}>
                  <div style={{ fontWeight: 600 }}>{approverName}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{approvedAt || '-'}</div>
                </div>
              );
            }}
          </Cell>
        </Column>
      ) : null}

      {showQboReceipt ? (
        <Column width={110}>
          <HeaderCell>Receipt in QBO</HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => {
              if (isSummaryRow(rowData)) return '';
              return (
                <Checkbox
                  checked={Boolean(rowData.receipt_uploaded_to_qbo)}
                  disabled={!onToggleQboReceipt}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(_value, checked, e) => {
                    e?.stopPropagation?.();
                    onToggleQboReceipt?.(rowData, checked);
                  }}
                />
              );
            }}
          </Cell>
        </Column>
      ) : null}

      {showActions ? (
      <Column width={actionsColumnWidth} fixed="right">
        <HeaderCell>Actions</HeaderCell>
        <Cell>
          {(rowData) => (
            isSummaryRow(rowData) ? null : (
            <div>
              <ButtonGroup size="xs">
                {onManageAttachments ? (
                  <Button
                    appearance="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageAttachments?.(rowData);
                    }}
                  >
                    Attachments
                  </Button>
                ) : null}

                {showApprovalActions ? (
                  <PermissionGate permission="approve_expenses" fallback={null}>
                    {(() => {
                      const rowStatus = String(rowData.status || '').toLowerCase();
                      const isPending = rowStatus === 'pending';
                      const isSelfExpense = Boolean(currentEmployeeId) && String(rowData.employee_id || '') === String(currentEmployeeId);
                      const disableApproval = !isPending || isSelfExpense;

                      if (onReview) {
                        return (
                          <Button
                            appearance="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onReview?.(rowData);
                            }}
                            disabled={disableApproval}
                          >
                            Review
                          </Button>
                        );
                      }

                      return (
                        <>
                          <Button
                            appearance="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onApprove?.(rowData);
                            }}
                            disabled={disableApproval}
                          >
                            Approve
                          </Button>
                          <Button
                            color="red"
                            appearance="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              onReject?.(rowData);
                            }}
                            disabled={disableApproval}
                          >
                            Reject
                          </Button>
                        </>
                      );
                    })()}
                  </PermissionGate>
                ) : null}

                {onMarkPaid ? (
                  <PermissionGate permission="approve_expenses" fallback={null}>
                    <Button
                      appearance="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkPaid?.(rowData);
                      }}
                      disabled={String(rowData.status).toLowerCase() !== 'approved' || Boolean(rowData.paid_on)}
                    >
                      Mark Paid
                    </Button>
                  </PermissionGate>
                ) : null}

                {/* Invoicing bills the expense to its customer, so it is only
                    offered on a settled expense that is marked customer
                    reimbursable and has not been invoiced already — the same
                    conditions the board and the server enforce. */}
                {onMarkInvoiced ? (
                  <PermissionGate permission="approve_expenses" fallback={null}>
                    <Button
                      appearance="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkInvoiced?.(rowData);
                      }}
                      disabled={
                        String(rowData.status).toLowerCase() !== 'approved'
                        || !rowData.customer_reimbursable
                        || Boolean(rowData.invoiced_on)
                        || !(rowData.paid_on || rowData.purchased_by_me)
                      }
                    >
                      Mark Invoiced
                    </Button>
                  </PermissionGate>
                ) : null}

                {showCancelAction && currentEmployeeId && String(rowData.employee_id || '') === String(currentEmployeeId) ? (
                  <Button
                    appearance="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancel?.(rowData);
                    }}
                    disabled={String(rowData.status).toLowerCase() !== 'pending'}
                  >
                    Cancel
                  </Button>
                ) : null}
              </ButtonGroup>
              {showApprovalActions
                && String(rowData.status || '').toLowerCase() === 'pending'
                && currentEmployeeId
                && String(rowData.employee_id || '') === String(currentEmployeeId) ? (
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                    You cannot approve your own request
                  </div>
                ) : null}
            </div>
            )
          )}
        </Cell>
      </Column>
      ) : null}
    </Table>
  );
}
